<template>
  <q-dialog :model-value="modelValue" persistent @update:model-value="$emit('update:modelValue', $event)">
    <q-card style="min-width: 600px">
      <q-card-section class="bg-primary text-white">
        <div class="text-h6">Add Device Part to Registry</div>
        <div class="text-caption">
          Create a new device part that can be used across multiple equipment instances
        </div>
      </q-card-section>

      <q-card-section>
        <div class="q-gutter-md">
          <q-input
            v-model="partForm.part_number"
            label="Part Number"
            outlined
            dense
            autofocus
            hint="e.g., ABB-ACS580-01-025A-4"
          />

          <q-input
            v-model="partForm.manufacturer"
            label="Manufacturer"
            outlined
            dense
            hint="e.g., ABB, Schneider Electric, Siemens"
          />

          <q-input
            v-model="partForm.model"
            label="Model"
            outlined
            dense
            hint="e.g., ACS580, M241, S7-1200"
          />

          <q-select
            v-model="partForm.brick_class"
            label="BRICK Class"
            :options="brickClassOptions"
            outlined
            dense
            emit-value
            map-options
            :loading="loadingHierarchy"
            hint="Select BRICK class (from most specific to most general)"
            @update:model-value="handleBrickClassChange"
          >
            <template #prepend>
              <q-icon name="account_tree" />
            </template>
            <template #option="scope">
              <q-item v-bind="scope.itemProps">
                <q-item-section>
                  <q-item-label>{{ scope.opt.label }}</q-item-label>
                  <q-item-label caption>{{ scope.opt.description }}</q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </q-select>

          <q-expansion-item
            label="Specifications"
            icon="settings"
            default-opened
          >
            <q-card>
              <q-card-section>
                <div v-if="loadingSpecFields" class="text-center q-pa-md">
                  <q-spinner color="primary" size="40px" />
                  <div class="text-caption q-mt-sm">Loading specification fields...</div>
                </div>
                <div v-else-if="specificationFields.length === 0" class="text-center text-grey-6 q-pa-md">
                  Select a BRICK class to see relevant specification fields
                </div>
                <div v-else class="q-gutter-md">
                  <template v-for="field in specificationFields" :key="field.key">
                    <!-- Text and Number fields -->
                    <q-input
                      v-if="field.type === 'text' || field.type === 'number'"
                      v-model="partForm.specifications[field.key]"
                      :label="field.label"
                      :type="field.type"
                      outlined
                      dense
                      :hint="field.hint"
                    />

                    <!-- Textarea fields -->
                    <q-input
                      v-else-if="field.type === 'textarea'"
                      v-model="partForm.specifications[field.key]"
                      :label="field.label"
                      type="textarea"
                      rows="3"
                      outlined
                      dense
                      :hint="field.hint"
                    />

                    <!-- Select dropdown fields -->
                    <q-select
                      v-else-if="field.type === 'select'"
                      v-model="partForm.specifications[field.key]"
                      :label="field.label"
                      :options="field.options"
                      outlined
                      dense
                      emit-value
                      map-options
                      clearable
                      :hint="field.hint"
                    />

                    <!-- Dependent select fields (signal_subtype depends on signal_type) -->
                    <q-select
                      v-else-if="field.type === 'select_dependent'"
                      v-model="partForm.specifications[field.key]"
                      :label="field.label"
                      :options="getDependentOptions(field)"
                      outlined
                      dense
                      emit-value
                      map-options
                      clearable
                      :disable="!partForm.specifications[field.depends_on]"
                      :hint="field.hint"
                    />
                  </template>
                </div>
              </q-card-section>
            </q-card>
          </q-expansion-item>
        </div>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn flat label="Cancel" @click="handleCancel" />
        <q-btn
          color="primary"
          label="Create Part"
          :loading="creating"
          :disable="!partForm.part_number || !partForm.manufacturer || !partForm.model || !partForm.brick_class"
          @click="handleCreate"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup>
  import { ref, watch } from 'vue';
  import { useQuasar } from 'quasar';
  import { api } from '@/services/api';

  const props = defineProps({
    modelValue: {
      type: Boolean,
      required: true,
    },
    partNumber: {
      type: String,
      default: '',
    },
    brickClass: {
      type: String,
      default: '',
    },
  });

  const emit = defineEmits(['update:modelValue', 'created']);

  const $q = useQuasar();

  // Form state
  const partForm = ref({
    part_number: '',
    manufacturer: '',
    model: '',
    brick_class: '',
    specifications: {},
  });

  const brickClassOptions = ref([]);
  const specificationFields = ref([]);
  const loadingHierarchy = ref(false);
  const loadingSpecFields = ref(false);
  const creating = ref(false);

  // Initialize form when dialog opens
  watch(() => props.modelValue, async (isOpen) => {
    if (isOpen) {
      // Pre-fill with props
      partForm.value = {
        part_number: props.partNumber || '',
        manufacturer: '',
        model: '',
        brick_class: props.brickClass || '',
        specifications: {},
      };

      // Load BRICK class hierarchy if brick_class provided
      if (props.brickClass) {
        await loadBrickHierarchy(props.brickClass);
        await loadSpecificationFields(props.brickClass);
      }
    }
  }, { immediate: true });

  const loadBrickHierarchy = async (brickClass) => {
    if (!brickClass) {return;}

    loadingHierarchy.value = true;
    try {
      const response = await api.get('/brick-schema/hierarchy', {
        brick_class: brickClass,
      });

      const hierarchy = response.data || response || [];

      // Transform hierarchy into dropdown options with descriptions
      brickClassOptions.value = hierarchy.map((cls, index) => {
        let description = '';
        if (index === 0) {
          description = 'Most specific - exact match for this device';
        } else if (index === hierarchy.length - 1) {
          description = 'Most general - works for any related device';
        } else {
          description = `Parent class (level ${index})`;
        }

        return {
          label: cls,
          value: cls,
          description: description,
        };
      });
    } catch (error) {
      // Fallback to just the current class
      brickClassOptions.value = [{
        label: brickClass,
        value: brickClass,
        description: 'Current device class',
      }];
    } finally {
      loadingHierarchy.value = false;
    }
  };

  const loadSpecificationFields = async (brickClass) => {
    if (!brickClass) {
      specificationFields.value = [];
      partForm.value.specifications = {};
      return;
    }

    loadingSpecFields.value = true;
    try {
      const response = await api.get('/brick-schema/specification-fields', {
        brick_class: brickClass,
      });

      specificationFields.value = response.data || response || [];

      // Initialize specifications object with empty values for all fields
      const specs = {};
      specificationFields.value.forEach((field) => {
        specs[field.key] = partForm.value.specifications[field.key] || '';
      });
      partForm.value.specifications = specs;
    } catch (error) {
      // Fallback to generic fields
      specificationFields.value = [
        { key: 'description', label: 'Description', type: 'textarea', hint: 'Technical specifications' },
      ];
      partForm.value.specifications = { description: '' };
    } finally {
      loadingSpecFields.value = false;
    }
  };

  const handleBrickClassChange = async (brickClass) => {
    if (brickClass) {
      await loadSpecificationFields(brickClass);
    }
  };

  const getDependentOptions = (field) => {
    // Get options based on the value of the field it depends on
    if (!field.depends_on || !field.options) {
      return [];
    }

    const dependsOnValue = partForm.value.specifications[field.depends_on];
    if (!dependsOnValue) {
      return [];
    }

    return field.options[dependsOnValue] || [];
  };

  const handleCancel = () => {
    emit('update:modelValue', false);
    // Reset form
    partForm.value = {
      part_number: '',
      manufacturer: '',
      model: '',
      brick_class: '',
      specifications: {},
    };
    brickClassOptions.value = [];
    specificationFields.value = [];
  };

  const handleCreate = async () => {
    creating.value = true;

    try {
      // Clean up specifications - remove empty fields
      const specs = {};
      Object.entries(partForm.value.specifications).forEach(([key, value]) => {
        if (value && String(value).trim()) {
          specs[key] = value;
        }
      });

      // Convert specs object to array if empty (PHP expects array, not object)
      const specsToSend = Object.keys(specs).length > 0 ? specs : [];

      const response = await api.post('/device-parts', {
        part_number: partForm.value.part_number,
        manufacturer: partForm.value.manufacturer,
        model: partForm.value.model,
        brick_class: partForm.value.brick_class,
        specifications: specsToSend,
      });

      // Handle wrapped response
      const part = response.data || response;

      $q.notify({
        type: 'positive',
        message: `Device part "${partForm.value.part_number}" created successfully`,
      });

      // Emit created event with the new part
      emit('created', part);

      // Close dialog
      emit('update:modelValue', false);

      // Reset form
      partForm.value = {
        part_number: '',
        manufacturer: '',
        model: '',
        brick_class: '',
        specifications: {},
      };
      brickClassOptions.value = [];
      specificationFields.value = [];
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: error.response?.data?.message || 'Failed to create device part',
      });
    } finally {
      creating.value = false;
    }
  };
</script>
